// REQ-395/396: note tool constants — the font-size preset list and the text-color palette the
// tool-options bar's two pickers show, plus each field's starting value. Mirrors
// `figureHighlight.constants.ts`'s role for PR #1381's highlight tool (single source for a
// palette + a catalogue + starting values), trimmed to the two fields a note actually has
// ("Font size and text color controls only" per the issue).

/**
 * Preset font sizes shown in the tool-options bar's dropdown. Figma's frame
 * (`local-docs/plans/figma-notes.md`, `18916:553778`) listed 2..18; "2" is dropped because the
 * backend enforces `font_size` as an integer in `4..200` (verified via `pnpm run api:show`), so it
 * would 400 if picked. The larger steps past 18 are added on top of the design's list — 18pt is
 * unreadably small on a drawing viewed at typical zoom, and the backend's ceiling is 200, so the
 * design's range was the binding constraint rather than the contract. Familiar word-processor
 * ladder from 20 up, coarsening as it grows.
 *
 * Every entry must stay inside 4..200 — `note.constants.test.ts` asserts exactly that, so an
 * out-of-range preset cannot ship.
 */
export const NOTE_FONT_SIZE_OPTIONS: readonly number[] = [
	4, 6, 8, 10, 12, 14, 16, 18, 20, 24, 28, 36, 48, 72, 96, 144,
]

// Default trigger value in the Figma "Note tool armed" frame ("Text 12 ⌄").
export const DEFAULT_NOTE_FONT_SIZE = 12

/**
 * 17-swatch palette (6-per-row, last row 5) — exact hex values pulled live via `get_design_context`
 * on the swatch-grid frame (node `18916:553878`, fill colors read directly off each of its 17
 * child swatches). Byte-identical to PR #1381's own `HIGHLIGHT_PALETTE`
 * (`interactions/figureHighlight.constants.ts`, unmerged) — confirms this is one shared
 * design-system palette component, not a coincidence. Kept as its own constant rather than a
 * shared import: that file doesn't exist on this branch (PR #1381 isn't merged), and importing
 * across an unmerged PR's files isn't possible.
 */
export const NOTE_TEXT_COLOR_PALETTE: readonly string[] = [
	"#FF0000",
	"#FF9000",
	"#FFFF00",
	"#56B300",
	"#0096FF",
	"#FF00FF",
	"#EA5984",
	"#FF8351",
	"#FFEE67",
	"#6BDB61",
	"#81BAFF",
	"#FF7FFF",
	"#FFFFFF",
	"#C0C0C0",
	"#808080",
	"#404040",
	"#000000",
]

// Red, not the black the Figma frame shows selected: notes in the users' own PDFs are mostly red
// (PR #1386 review), so it is the colour that needs the fewest clicks. First swatch of the palette.
export const DEFAULT_NOTE_TEXT_COLOR = "#FF0000"
